import React, { useEffect, useRef, useState } from 'react';
import { useAdsAllowed } from '../hooks/useAdsAllowed';
import { trackEvent } from '../services/analytics';

/**
 * The single reusable ad unit. Every placement uses this.
 *
 * Responsibilities (see ADS_INTEGRATION.md):
 *   - Reserves a fixed min-height per format  -> zero CLS (Core Web Vitals; CLS
 *     also tanks RPM).
 *   - Lazy-loads below-the-fold via IntersectionObserver -> faster LCP, more
 *     viewable impressions = higher vCPM.
 *   - Consent + reader-route aware via useAdsAllowed() -> never renders on the
 *     reader, never requests before consent.
 *   - Network-agnostic: injects Google AdSense when REACT_APP_ADSENSE_CLIENT is
 *     set (works as the Ezoic backfill demand source too). Until a network is
 *     configured it shows a labelled placeholder ONLY in dev, and renders null
 *     in production — so the live site is never broken by an un-filled slot.
 *
 * Usage:  <AdSlot id="results-top" format="leaderboard" />
 */

// Reserved heights keep layout stable before the ad lands. Tune per real fill.
const FORMAT_MIN_HEIGHT = {
  leaderboard: 90,    // 728x90 / responsive top
  'in-grid': 250,     // native in-feed rectangle
  sidebar: 250,       // 300x250 medium rectangle
  'sticky-footer': 50, // 320x50 mobile anchor
};

const ADSENSE_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT || '';
// Map our semantic slot ids -> AdSense ad-unit ids (set once units exist).
// e.g. REACT_APP_ADSENSE_SLOTS='results-top:1234567890,results-ingrid:0987654321'
const ADSENSE_SLOTS = (() => {
  const map = {};
  (process.env.REACT_APP_ADSENSE_SLOTS || '').split(',').forEach((pair) => {
    const [k, v] = pair.split(':').map((s) => (s || '').trim());
    if (k && v) map[k] = v;
  });
  return map;
})();

let adsenseScriptLoaded = false;
const ensureAdSenseScript = () => {
  if (adsenseScriptLoaded || !ADSENSE_CLIENT) return;
  adsenseScriptLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.crossOrigin = 'anonymous';
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
  document.head.appendChild(s);
};

const AdSlot = ({ id, format = 'leaderboard', className = '' }) => {
  const allowed = useAdsAllowed();
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const viewedRef = useRef(false);
  const minHeight = FORMAT_MIN_HEIGHT[format] || 90;
  const isDev = process.env.NODE_ENV !== 'production';
  const configured = Boolean(ADSENSE_CLIENT);

  // Lazy reveal — only act once the slot scrolls near the viewport.
  useEffect(() => {
    if (!allowed || !ref.current) return undefined;
    const el = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin: '200px' } // start loading a bit before it's visible
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [allowed]);

  // Request the ad + fire a viewability event the first time it's in view.
  useEffect(() => {
    if (!allowed || !inView || viewedRef.current) return;
    viewedRef.current = true;
    trackEvent('Ad Slot Viewed', { slot: id, format });
    if (configured) {
      ensureAdSenseScript();
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) {
        /* adsbygoogle not ready yet; the <ins> will fill once the script loads */
      }
    }
  }, [allowed, inView, id, format, configured]);

  // Reader route or no consent -> render nothing at all.
  if (!allowed) return null;

  return (
    <div
      ref={ref}
      className={`ad-slot ad-slot--${format} my-6 mx-auto w-full max-w-3xl ${className}`}
      style={{ minHeight }}
      data-ad-slot-id={id}
      aria-label="advertisement"
    >
      {configured ? (
        <ins
          className="adsbygoogle"
          style={{ display: 'block', minHeight }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={ADSENSE_SLOTS[id] || ''}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      ) : (
        isDev && (
          <div
            className="flex items-center justify-center text-[11px] uppercase tracking-widest text-gray-400 border border-dashed border-gray-300 rounded-lg bg-gray-50"
            style={{ minHeight }}
          >
            Ad · {id} · {format}
          </div>
        )
      )}
    </div>
  );
};

export default AdSlot;
